import { makeAutoObservable } from 'mobx'
import { type AnalyticsChartPeriod } from 'entities/Analytics'
import {
  type AnalyticsResponse,
  type IContactsChartResponse,
  type IContactsStatisticResponse,
  type ProcessedStatistic,
} from 'entities/Analytics/api/types'
import { formatDateForDisplay, calculateTrend, computeHasStatistic } from 'pages/analytics/lib'

export interface ContactsRechartsDataPoint {
  date: string
  name: string
  new_contacts: number
  opted_out: number
}

export class ContactsChartDataStore {
  private _rawStatistic: IContactsStatisticResponse[] = []
  private _rawChartData: IContactsChartResponse[] = []
  private _period: AnalyticsChartPeriod | null = null

  constructor() {
    makeAutoObservable(this)
  }

  setRawData = (
    response: AnalyticsResponse<IContactsStatisticResponse, IContactsChartResponse>,
    period?: AnalyticsChartPeriod
  ) => {
    this._rawStatistic = response.statistic
    this._rawChartData = response.charts
    if (period) {
      this._period = period
    }
  }

  get processedStatistic(): ProcessedStatistic[] {
    return this._rawStatistic.map((item) => {
      const { key, value, prevValue, label } = item
      const trend = calculateTrend(value, prevValue)

      return {
        key: key,
        label: label || key,
        value: value,
        prevValue: prevValue,
        trend,
      }
    })
  }

  get rechartsData(): ContactsRechartsDataPoint[] {
    return this._rawChartData
      .map((item) => ({
        date: item.date,
        name: formatDateForDisplay(item.date, this._period ?? 'week'),
        new_contacts: item.new_contacts || 0,
        opted_out: item.opted_out || 0,
      }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
  }

  getStatisticByKey = (key: string): ProcessedStatistic | undefined => {
    return this.processedStatistic.find((item) => item.key === key)
  }

  get hasStatistic() {
    return computeHasStatistic(this._rawStatistic)
  }

  dispose = () => {
    this._rawStatistic = []
    this._rawChartData = []
    this._period = null
  }
}
